import type { PuckComponent } from "../ai/puck-types.js";
import type { AuditFeedback, AuditResult } from "../types.js";
import type { SeoMeta } from "../schema/index.js";
import {
  auditAltText,
  auditHeadings,
  calculateSeoScore,
  checkCongruence,
  generateMetaSuggestion,
  type AltTextAudit,
  type HeadingAudit,
  type MetaSuggestion,
  type SeoScore,
} from "../seo/seo-analyzer.js";
import { generateSchemaOrg, type SchemaOrgOutput } from "../seo/schema-org-generator.js";
import {
  calculateConversionScore,
  type ConversionScore,
} from "./conversion-scorer.js";

const MAX_RECOMMENDATIONS = 12;

export interface OptimizationSeoInput extends SeoMeta {
  slug?: string;
  focusKeyword?: string;
}

export interface OptimizationRecommendation {
  id: string;
  category: "conversion" | "seo" | "performance";
  impact: "high" | "medium" | "low";
  title: string;
  description: string;
  action: string | null;
}

export interface OptimizationPerformanceResult {
  score: number | null;
  status: "measured" | "unavailable";
  signalStatus: AuditFeedback["status"];
  source: AuditFeedback["source"] | null;
  audit: AuditResult | null;
  notes: string[];
  issues: string[];
  suggestions: string[];
}

export interface OptimizationSeoResult {
  score: SeoScore;
  metaSuggestion: MetaSuggestion;
  altTextAudit: AltTextAudit;
  headingAudit: HeadingAudit;
  congruence: { score: number; issues: string[] };
  schema: SchemaOrgOutput;
}

export interface OptimizationHeatmapItem {
  componentId: string | null;
  componentType: string;
  componentIndex: number;
  attention: number;
  reasons: string[];
}

export interface OptimizationAnalysisResult {
  pageId: string | null;
  versionId: string | null;
  scores: {
    overall: number;
    conversion: number;
    seo: number;
    performance: number | null;
  };
  conversion: ConversionScore;
  seo: OptimizationSeoResult;
  performance: OptimizationPerformanceResult;
  recommendations: OptimizationRecommendation[];
  heatmap: OptimizationHeatmapItem[];
}

export interface OptimizationAnalysisInput {
  pageId?: string | null;
  versionId?: string | null;
  pageUrl?: string | null;
  components: PuckComponent[];
  seo?: OptimizationSeoInput;
  latestAudit?: AuditResult | null;
  auditFeedback?: AuditFeedback | null;
}

export interface OptimizationAnalysisDraftInput {
  components?: PuckComponent[];
  seo?: OptimizationSeoInput;
  pageUrl?: string | null;
}

export interface OptimizationApplyDraftInput extends OptimizationAnalysisDraftInput {
  recommendationIds?: string[];
}

export interface OptimizationAppliedRecommendation {
  recommendationId: string;
  label: string;
}

export interface OptimizationSkippedRecommendation {
  recommendationId: string;
  reason: string;
}

export interface OptimizationApplyResult {
  pageId: string | null;
  versionId: string | null;
  components: PuckComponent[];
  seo: OptimizationSeoInput;
  applied: OptimizationAppliedRecommendation[];
  skipped: OptimizationSkippedRecommendation[];
  analysis: OptimizationAnalysisResult;
}

const AUTO_APPLY_RECOMMENDATION_IDS = new Set<string>([
  "conversion:cta",
  "conversion:form_friction",
  "conversion:headline",
  "conversion:hierarchy",
  "seo:meta",
  "seo:schema",
  "seo:alt",
  "seo:headings",
  "seo:slug-format",
  "seo:focus-keyword",
]);

export function analyzeOptimization(
  input: OptimizationAnalysisInput,
): OptimizationAnalysisResult {
  const seoInput = input.seo ?? {};
  const pageUrl = input.pageUrl ?? buildPageUrl(seoInput.slug);
  const normalizedComponents = input.components.map(normalizeOptimizationComponent);
  const conversion = calculateConversionScore(normalizedComponents);
  const metaSuggestion = generateMetaSuggestion(normalizedComponents);
  const altTextAudit = auditAltText(normalizedComponents);
  const headingAudit = auditHeadings(normalizedComponents);
  const congruence = checkCongruence(normalizedComponents);
  const schema = generateSchemaOrg(normalizedComponents, pageUrl);
  const heatmap = buildOptimizationHeatmap(normalizedComponents);
  const seoScore = calculateSeoScore(normalizedComponents, {
    hasSchema: hasSchemaMarkup(seoInput, schema),
    metaTitle: seoInput.title,
    metaDescription: seoInput.description,
    pageUrl,
  });
  const performance = buildPerformanceResult(input.auditFeedback ?? null, input.latestAudit ?? null);
  const recommendations = collectRecommendations({
    conversion,
    seo: {
      score: seoScore,
      metaSuggestion,
      altTextAudit,
      headingAudit,
      congruence,
      schema,
    },
    performance,
    focusKeyword: seoInput.focusKeyword,
    slug: seoInput.slug,
  });

  return {
    pageId: input.pageId ?? null,
    versionId: input.versionId ?? null,
    scores: {
      overall: calculateOverallScore(conversion.total, seoScore.total, performance.score),
      conversion: conversion.total,
      seo: seoScore.total,
      performance: performance.score,
    },
    conversion,
    seo: {
      score: seoScore,
      metaSuggestion,
      altTextAudit,
      headingAudit,
      congruence,
      schema,
    },
    performance,
    recommendations,
    heatmap,
  };
}

export function canAutoApplyOptimizationRecommendation(
  recommendationId: string,
): boolean {
  return AUTO_APPLY_RECOMMENDATION_IDS.has(recommendationId);
}

export function applyOptimizationRecommendations(
  input: OptimizationAnalysisInput & { recommendationIds?: string[] },
): OptimizationApplyResult {
  const seo = cloneSeo(input.seo ?? {});
  const components = cloneComponents(input.components);
  const initialAnalysis = analyzeOptimization(input);
  const selectedRecommendationIds = input.recommendationIds?.length
    ? input.recommendationIds
    : initialAnalysis.recommendations.map((recommendation) => recommendation.id);
  const recommendationMap = new Map(
    initialAnalysis.recommendations.map((recommendation) => [recommendation.id, recommendation]),
  );
  const applied: OptimizationAppliedRecommendation[] = [];
  const skipped: OptimizationSkippedRecommendation[] = [];

  for (const recommendationId of selectedRecommendationIds) {
    const recommendation = recommendationMap.get(recommendationId);

    if (!recommendation) {
      skipped.push({
        recommendationId,
        reason: "Recommendation is not available for the current draft state",
      });
      continue;
    }

    const result = applyRecommendation({
      recommendation,
      components,
      seo,
      analysis: initialAnalysis,
    });

    if (result.applied) {
      applied.push({
        recommendationId,
        label: result.label ?? recommendation.title,
      });
      continue;
    }

    skipped.push({
      recommendationId,
      reason: result.reason ?? "No deterministic auto-fix is available for this recommendation",
    });
  }

  const analysis = analyzeOptimization({
    ...input,
    components,
    seo,
  });

  return {
    pageId: input.pageId ?? null,
    versionId: input.versionId ?? null,
    components,
    seo,
    applied,
    skipped,
    analysis,
  };
}

export function normalizeOptimizationSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildPageUrl(slug?: string | null): string {
  if (!slug) {
    return "/";
  }

  return slug.startsWith("/") ? slug : `/${slug}`;
}

function cloneComponents(components: PuckComponent[]): PuckComponent[] {
  return components.map((component) => ({
    ...component,
    props: { ...component.props },
  }));
}

function cloneSeo(seo: OptimizationSeoInput): OptimizationSeoInput {
  return {
    ...seo,
    ...(Array.isArray(seo.jsonLd)
      ? {
        jsonLd: seo.jsonLd.map((item) => ({ ...item })),
      }
      : {}),
  };
}

function normalizeOptimizationComponent(component: PuckComponent): PuckComponent {
  const kind = toComponentKind(component.type);
  const props = { ...component.props };

  if (typeof props.ctaLabel === "string" && typeof props.buttonText !== "string") {
    props.buttonText = props.ctaLabel;
  }
  if (typeof props.subtitle === "string" && typeof props.subheading !== "string") {
    props.subheading = props.subtitle;
  }
  if (kind === "button" && typeof props.label === "string" && typeof props.buttonText !== "string") {
    props.buttonText = props.label;
  }
  if (kind === "heading" && typeof props.level === "string") {
    const match = props.level.match(/^h([1-6])$/i);
    if (match) {
      props.level = Number(match[1]);
    }
  }

  return {
    ...component,
    type: kind,
    props,
  };
}

function toComponentKind(type: string): string {
  return type
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
}

function applyRecommendation(input: {
  recommendation: OptimizationRecommendation;
  components: PuckComponent[];
  seo: OptimizationSeoInput;
  analysis: OptimizationAnalysisResult;
}): { applied: boolean; label?: string; reason?: string } {
  const { recommendation, components, seo, analysis } = input;

  switch (recommendation.id) {
    case "conversion:cta":
      return applyCtaRecommendation(components);
    case "conversion:form_friction":
      return applyFormFrictionRecommendation(components);
    case "conversion:headline":
      return applyHeadlineRecommendation(components);
    case "conversion:hierarchy":
      return applyHierarchyRecommendation(components);
    case "seo:meta":
      return applyMetaRecommendation(seo, analysis);
    case "seo:schema":
      return applySchemaRecommendation(seo, analysis);
    case "seo:alt":
      return applyAltRecommendation(components, analysis);
    case "seo:headings":
      return applyHeadingRecommendation(components);
    case "seo:slug-format":
      return applySlugRecommendation(seo);
    case "seo:focus-keyword":
      return applyFocusKeywordRecommendation(seo, analysis);
    default:
      return {
        applied: false,
        reason: canAutoApplyOptimizationRecommendation(recommendation.id)
          ? "This recommendation could not be applied to the current draft state"
          : "This recommendation is insight-only and requires manual design changes",
      };
  }
}

function applyCtaRecommendation(
  components: PuckComponent[],
): { applied: boolean; label?: string; reason?: string } {
  // C1-02: Removed dead `index >= 0` guard — findIndex callback index is always >= 0
  const ctaIndex = components.findIndex((component) =>
    hasButtonText(component),
  );

  if (ctaIndex >= 0) {
    const targetIndex = findHeroIndex(components) === 0 ? 1 : 0;
    if (ctaIndex <= targetIndex || ctaIndex < 3) {
      return {
        applied: false,
        reason: "Primary CTA is already above the fold",
      };
    }

    const [ctaComponent] = components.splice(ctaIndex, 1);
    if (!ctaComponent) {
      return { applied: false, reason: "CTA component could not be moved" };
    }
    components.splice(targetIndex, 0, ctaComponent);
    return { applied: true, label: "Moved primary CTA above the fold" };
  }

  const heroIndex = findHeroIndex(components);
  if (heroIndex < 0) {
    return {
      applied: false,
      reason: "No hero section is available to receive a primary CTA",
    };
  }

  const hero = components[heroIndex]!;
  if (typeof hero.props.ctaLabel === "string" || typeof hero.props.buttonText === "string") {
    return { applied: false, reason: "Hero already exposes a CTA" };
  }

  hero.props.ctaLabel = "Get Started";
  if (typeof hero.props.buttonText !== "string") {
    hero.props.buttonText = "Get Started";
  }
  if (typeof hero.props.ctaUrl !== "string") {
    hero.props.ctaUrl = "#contact";
  }
  return { applied: true, label: "Added a primary CTA to the hero" };
}

function applyFormFrictionRecommendation(
  components: PuckComponent[],
): { applied: boolean; label?: string; reason?: string } {
  for (const component of components) {
    if (!isFormComponent(component)) {
      continue;
    }

    if (!Array.isArray(component.props.fields) || component.props.fields.length <= 4) {
      continue;
    }

    component.props.fields = component.props.fields.slice(0, 4);
    return { applied: true, label: "Reduced form fields to the first four" };
  }

  return {
    applied: false,
    reason: "No oversized form fields array was found in the current draft",
  };
}

function applyHeadlineRecommendation(
  components: PuckComponent[],
): { applied: boolean; label?: string; reason?: string } {
  const heroIndex = findHeroIndex(components);
  if (heroIndex < 0) {
    return { applied: false, reason: "No hero section is available to update the headline" };
  }

  const hero = components[heroIndex]!;
  const heading = readHeading(hero);

  if (!heading) {
    return {
      applied: false,
      reason: "No existing headline text is available to rewrite automatically",
    };
  }

  if (heading.length <= 80) {
    return {
      applied: false,
      reason: "Headline length is already within the target range",
    };
  }

  writeHeading(hero, `${heading.slice(0, 77).trimEnd()}...`);
  return { applied: true, label: "Shortened the hero headline" };
}

function applyHierarchyRecommendation(
  components: PuckComponent[],
): { applied: boolean; label?: string; reason?: string } {
  const heroIndex = findHeroIndex(components);
  if (heroIndex < 0) {
    return { applied: false, reason: "No hero section is available to move to the top" };
  }
  if (heroIndex === 0) {
    return { applied: false, reason: "Hero section is already first" };
  }

  const [hero] = components.splice(heroIndex, 1);
  if (!hero) {
    return { applied: false, reason: "Hero section could not be moved" };
  }
  components.unshift(hero);
  return { applied: true, label: "Moved the hero section to the top" };
}

function applyMetaRecommendation(
  seo: OptimizationSeoInput,
  analysis: OptimizationAnalysisResult,
): { applied: boolean; label?: string; reason?: string } {
  let changed = false;

  if (!seo.title && analysis.seo.metaSuggestion.title) {
    seo.title = analysis.seo.metaSuggestion.title;
    changed = true;
  }
  if (!seo.description && analysis.seo.metaSuggestion.description) {
    seo.description = analysis.seo.metaSuggestion.description;
    changed = true;
  }

  return changed
    ? { applied: true, label: "Filled the missing SEO meta fields" }
    : { applied: false, reason: "Meta title and description are already present" };
}

function applySchemaRecommendation(
  seo: OptimizationSeoInput,
  analysis: OptimizationAnalysisResult,
): { applied: boolean; label?: string; reason?: string } {
  if (Array.isArray(seo.jsonLd) && seo.jsonLd.length > 0) {
    return { applied: false, reason: "Schema markup is already present" };
  }

  if (analysis.seo.schema.schemas.length === 0) {
    return { applied: false, reason: "No generated schema payload is available for this draft" };
  }

  seo.jsonLd = analysis.seo.schema.schemas.map((item) => ({ ...item }));
  return { applied: true, label: "Added generated Schema.org markup" };
}

function applyAltRecommendation(
  components: PuckComponent[],
  analysis: OptimizationAnalysisResult,
): { applied: boolean; label?: string; reason?: string } {
  let updatedCount = 0;

  for (const suggestion of analysis.seo.altTextAudit.suggestions) {
    const component = components[suggestion.componentIndex];
    if (!component) {
      continue;
    }

    if (!isDirectImageComponent(component)) {
      continue;
    }

    if (typeof component.props.alt === "string" && component.props.alt.trim().length > 0) {
      continue;
    }

    component.props.alt = suggestion.suggestedAlt;
    updatedCount += 1;
  }

  return updatedCount > 0
    ? { applied: true, label: `Added alt text to ${updatedCount} image block${updatedCount === 1 ? "" : "s"}` }
    : { applied: false, reason: "No direct image blocks without alt text were found" };
}

function applyHeadingRecommendation(
  components: PuckComponent[],
): { applied: boolean; label?: string; reason?: string } {
  const heroIndex = findHeroIndex(components);
  if (heroIndex >= 0) {
    const hero = components[heroIndex]!;
    if (readHeading(hero)) {
      return { applied: false, reason: "Hero section already supplies the primary heading" };
    }
  }

  const headingComponent = components.find((component) => toComponentKind(component.type) === "heading");
  if (!headingComponent) {
    return { applied: false, reason: "No heading block is available to promote to H1" };
  }

  headingComponent.props.level = typeof headingComponent.props.level === "number" ? 1 : "h1";
  return { applied: true, label: "Promoted the first heading block to H1" };
}

function applySlugRecommendation(
  seo: OptimizationSeoInput,
): { applied: boolean; label?: string; reason?: string } {
  if (!seo.slug) {
    return { applied: false, reason: "No slug is set for this draft" };
  }

  const nextSlug = normalizeOptimizationSlug(seo.slug);
  if (!nextSlug || nextSlug === seo.slug) {
    return { applied: false, reason: "Slug already uses lowercase kebab-case" };
  }

  seo.slug = nextSlug;
  return { applied: true, label: "Normalized the page slug" };
}

function applyFocusKeywordRecommendation(
  seo: OptimizationSeoInput,
  analysis: OptimizationAnalysisResult,
): { applied: boolean; label?: string; reason?: string } {
  const keyword = seo.focusKeyword?.trim();
  if (!keyword) {
    return { applied: false, reason: "No focus keyword is set for this draft" };
  }

  const currentTitle = seo.title?.trim() || analysis.seo.metaSuggestion.title;
  if (!currentTitle) {
    return { applied: false, reason: "No title is available to align with the focus keyword" };
  }

  if (currentTitle.toLowerCase().includes(keyword.toLowerCase())) {
    return { applied: false, reason: "Page title already includes the focus keyword" };
  }

  seo.title = `${keyword} | ${currentTitle}`.slice(0, 60).trimEnd();
  return { applied: true, label: "Aligned the title with the focus keyword" };
}

function findHeroIndex(components: PuckComponent[]): number {
  return components.findIndex((component) => {
    const kind = toComponentKind(component.type);
    return kind === "hero" || kind === "header";
  });
}

function isFormComponent(component: PuckComponent): boolean {
  const kind = toComponentKind(component.type);
  return kind === "form" || kind === "contact-form" || kind === "lead-form";
}

function hasButtonText(component: PuckComponent): boolean {
  const kind = toComponentKind(component.type);
  if (kind === "button" || kind === "cta") {
    return true;
  }

  return typeof component.props.buttonText === "string"
    || typeof component.props.ctaLabel === "string"
    || (kind === "hero" && typeof component.props.ctaLabel === "string");
}

function readHeading(component: PuckComponent): string {
  const heading = component.props.heading;
  if (typeof heading === "string" && heading.trim().length > 0) {
    return heading;
  }

  const title = component.props.title;
  if (typeof title === "string" && title.trim().length > 0) {
    return title;
  }

  return "";
}

function writeHeading(component: PuckComponent, value: string): void {
  if (typeof component.props.heading === "string" || !("title" in component.props)) {
    component.props.heading = value;
    return;
  }

  component.props.title = value;
}

function isDirectImageComponent(component: PuckComponent): boolean {
  return Boolean(
    component.props.imageUrl
    || component.props.src
    || component.props.image
    || toComponentKind(component.type) === "image",
  );
}

function hasSchemaMarkup(
  seo: OptimizationSeoInput,
  generatedSchema: SchemaOrgOutput,
): boolean {
  const explicitJsonLd = Array.isArray(seo.jsonLd) && seo.jsonLd.length > 0;
  return explicitJsonLd || generatedSchema.schemas.length > 0;
}

function buildPerformanceResult(
  feedback: AuditFeedback | null,
  fallbackAudit: AuditResult | null,
): OptimizationPerformanceResult {
  const audit = withLabTbtAlias(feedback?.audit ?? fallbackAudit ?? null);

  if (feedback?.status === "stale") {
    return {
      score: null,
      status: "unavailable",
      signalStatus: "stale",
      source: feedback.source,
      audit,
      notes: feedback.notes,
      issues: ["The latest Lighthouse audit does not match the current published page."],
      suggestions: ["Republish the page to refresh measured runtime guidance for the live surface"],
    };
  }

  if (!audit || audit.performanceScore == null) {
    return {
      score: null,
      status: "unavailable",
      signalStatus: feedback?.status ?? "missing",
      source: feedback?.source ?? null,
      audit: audit ?? null,
      notes: feedback?.notes ?? ["No published-page Lighthouse audit is stored for this page yet."],
      issues: ["No Lighthouse audit is available yet"],
      suggestions: ["Publish the page to queue a Lighthouse audit and unlock measured performance guidance"],
    };
  }

  const issues: string[] = [];
  const suggestions: string[] = [];

  if (audit.performanceScore < 90) {
    issues.push(`Performance score is ${audit.performanceScore}/100`);
    suggestions.push("Reduce render-blocking work and heavy media on the page");
  }
  if (audit.lcpMs != null && audit.lcpMs > 2500) {
    issues.push(`Largest Contentful Paint is ${audit.lcpMs}ms`);
    suggestions.push("Improve above-the-fold rendering and optimize the largest visual asset");
  }
  if (audit.tbtMs != null && audit.tbtMs > 200) {
    issues.push(`Total Blocking Time is ${audit.tbtMs}ms`);
    suggestions.push("Reduce main-thread blocking work and defer non-critical scripts; field INP needs real user monitoring before it can be claimed");
  }
  if (audit.cls != null && Number(audit.cls) > 0.1) {
    issues.push(`Cumulative Layout Shift is ${audit.cls}`);
    suggestions.push("Reserve space for dynamic content and images to prevent layout shifts");
  }
  if (audit.ttfbMs != null && audit.ttfbMs > 800) {
    issues.push(`Time to First Byte is ${audit.ttfbMs}ms`);
    suggestions.push("Improve backend response time and caching for the published page");
  }

  return {
    score: audit.performanceScore,
    status: "measured",
    signalStatus: feedback?.status ?? "measured",
    source: feedback?.source ?? "published_runtime_audit",
    audit,
    notes: feedback?.notes ?? ["This measured runtime audit matches the currently published page."],
    issues,
    suggestions,
  };
}

function calculateOverallScore(
  conversionScore: number,
  seoScore: number,
  performanceScore: number | null,
): number {
  const weightedScores = [
    { score: conversionScore, weight: 0.4 },
    { score: seoScore, weight: 0.35 },
    ...(performanceScore == null ? [] : [{ score: performanceScore, weight: 0.25 }]),
  ];

  const totalWeight = weightedScores.reduce((sum, item) => sum + item.weight, 0);
  const weightedTotal = weightedScores.reduce(
    (sum, item) => sum + (item.score * item.weight),
    0,
  );

  return Math.round(weightedTotal / totalWeight);
}

function withLabTbtAlias(audit: AuditResult | null): AuditResult | null {
  if (!audit) {
    return null;
  }

  return {
    ...audit,
    tbtMs: audit.tbtMs ?? audit.inpMs ?? null,
  };
}

function buildOptimizationHeatmap(
  components: PuckComponent[],
): OptimizationHeatmapItem[] {
  return components.map((component, index) => {
    const kind = toComponentKind(component.type);
    const reasons: string[] = [];
    let attention = 0.24;

    if (index === 0) {
      attention += 0.32;
      reasons.push("First section sits above the fold.");
    } else if (index === 1) {
      attention += 0.16;
      reasons.push("Second section still receives early-scroll attention.");
    } else if (index >= 4) {
      attention -= Math.min(0.14, (index - 3) * 0.04);
      reasons.push("Lower sections typically receive less attention.");
    }

    if (kind === "hero" || kind === "header") {
      attention += 0.18;
      reasons.push("Hero sections usually concentrate the strongest initial attention.");
    }
    if (hasButtonText(component)) {
      attention += 0.1;
      reasons.push("Visible CTA affordances attract attention.");
    }
    if (isFormComponent(component)) {
      attention += 0.08;
      reasons.push("Form blocks draw focused intent when they are visible.");
    }
    if (kind === "footer") {
      attention -= 0.08;
      reasons.push("Footer content usually receives the least attention.");
    }

    return {
      componentId: typeof component.props.id === "string" ? component.props.id : null,
      componentType: kind,
      componentIndex: index,
      attention: Math.max(0.05, Math.min(0.98, Number(attention.toFixed(2)))),
      reasons: reasons.length > 0 ? reasons : ["Attention estimate is based on section order only."],
    };
  });
}

function collectRecommendations(input: {
  conversion: ConversionScore;
  seo: OptimizationSeoResult;
  performance: OptimizationPerformanceResult;
  focusKeyword?: string;
  slug?: string;
}): OptimizationRecommendation[] {
  const recommendations: OptimizationRecommendation[] = [];

  for (const factor of input.conversion.factors) {
    if (factor.issues.length === 0) {
      continue;
    }

    recommendations.push({
      id: `conversion:${factor.id}`,
      category: "conversion",
      impact: scoreToImpact(factor.score),
      title: factor.name,
      description: factor.issues[0]!,
      action: factor.suggestions[0] ?? null,
    });
  }

  for (const factor of input.seo.score.factors) {
    if (factor.issues.length === 0) {
      continue;
    }

    recommendations.push({
      id: `seo:${factor.id}`,
      category: "seo",
      impact: scoreToImpact(factor.score),
      title: factor.name,
      description: factor.issues[0]!,
      action: factor.suggestions[0] ?? null,
    });
  }

  if (input.slug) {
    const isSlugClean = normalizeOptimizationSlug(input.slug) === input.slug;
    if (!isSlugClean) {
      recommendations.push({
        id: "seo:slug-format",
        category: "seo",
        impact: "medium",
        title: "URL Slug",
        description: "Slug should be lowercase and hyphenated",
        action: "Use kebab-case for the page slug",
      });
    }
  }

  if (input.focusKeyword) {
    const keyword = input.focusKeyword.toLowerCase();
    const titleIncludesKeyword = (input.seo.metaSuggestion.title ?? "").toLowerCase().includes(keyword);
    if (!titleIncludesKeyword) {
      recommendations.push({
        id: "seo:focus-keyword",
        category: "seo",
        impact: "low",
        title: "Focus Keyword",
        description: "Focus keyword is not reflected in the suggested title",
        action: "Include the focus keyword in the page title and main heading",
      });
    }
  }

  input.performance.issues.forEach((issue, index) => {
    recommendations.push({
      id: `performance:${index}`,
      category: "performance",
      impact: performanceImpact(issue),
      title: "Performance Audit",
      description: issue,
      action: input.performance.suggestions[index] ?? input.performance.suggestions[0] ?? null,
    });
  });

  return recommendations.slice(0, MAX_RECOMMENDATIONS);
}

function scoreToImpact(score: number): "high" | "medium" | "low" {
  if (score < 0.4) {
    return "high";
  }
  if (score < 0.75) {
    return "medium";
  }
  return "low";
}

function performanceImpact(issue: string): "high" | "medium" | "low" {
  if (issue.includes("Largest Contentful Paint") || issue.includes("Total Blocking Time")) {
    return "high";
  }
  if (issue.includes("Performance score") || issue.includes("Time to First Byte")) {
    return "medium";
  }
  return "low";
}
